import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiLogIn, FiLogOut, FiMaximize, FiMenu, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const Header = ({ toggleSidebar }) => {
  const location = useLocation();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const isMaps = location.pathname.startsWith('/maps');
  const { user, isAuthenticated, logout, logoutAll } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const dispatchMapEvent = (name) => {
    try {
      window.dispatchEvent(new CustomEvent(name));
    } catch {
      // no-op
    }
  };

  const handleToggleSidebar = (e) => {
    e.stopPropagation(); // Prevent the event from bubbling up
    if (toggleSidebar) toggleSidebar(e);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  return (
    <header className="text-black shadow-sm border-b border-green-900/10 bg-gradient-to-r from-[#faf5cc] via-[#fff1cc] to-[#f1f7d8]">
      <div className="w-full px-3 py-1">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {toggleSidebar && (
              <button
                type="button"
                onClick={handleToggleSidebar}
                data-sidebar-toggle
                aria-label="Toggle sidebar"
                className="inline-flex items-center justify-center w-8 h-8 rounded border border-green-900/20 bg-white/70 hover:bg-white text-black"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <img src="/logo192.png" alt="Logo" className="w-8 h-8 object-contain" />
            <div className="min-w-0 flex items-center gap-2">
              <div className="text-sm font-extrabold tracking-wide truncate">Rayat Connect</div>
              <span className="hidden sm:inline-flex text-[10px] px-2 py-0.5 rounded-full border border-green-900/15 bg-white/60 text-black font-bold">
                {isMaps ? 'Maps' : 'Workspace'}
              </span>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1">
            <select aria-label="App theme" value={preference} onChange={event => setPreference(event.target.value)}
              className="theme-select border rounded px-1 py-1 text-xs bg-white text-black max-w-[85px]">
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
            {/* Menu */}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-green-900/20 bg-white/70 hover:bg-white text-black text-xs font-semibold"
                aria-haspopup="menu"
                aria-expanded={menuOpen ? 'true' : 'false'}
              >
                <FiMenu size={14} aria-hidden="true" /> Menu <FiChevronDown size={14} aria-hidden="true" />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-1 w-44 rounded-lg border border-green-900/10 bg-white text-black shadow-lg overflow-hidden z-[1000000]"
                >
                  <div className="px-2 py-1 text-[10px] font-bold tracking-wide text-black bg-[#faf5cc]">
                    Navigate
                  </div>
                  <NavLink to="/maps" className="block px-3 py-2 text-xs hover:bg-green-50" role="menuitem" onClick={() => setMenuOpen(false)}>
                    Maps
                  </NavLink>

                  <div className="px-2 py-1 text-[10px] font-bold tracking-wide text-black bg-[#faf5cc] border-t border-green-900/10">
                    Actions
                  </div>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      dispatchMapEvent('rayat:maps:refresh');
                    }}
                    disabled={!isMaps}
                  >
                    <span className="inline-flex items-center gap-2"><FiRefreshCw size={14} aria-hidden="true" /> Refresh map data</span>
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      dispatchMapEvent('rayat:maps:fit');
                    }}
                    disabled={!isMaps}
                  >
                    <span className="inline-flex items-center gap-2"><FiMaximize size={14} aria-hidden="true" /> Fit all institutes</span>
                  </button>

                  <div className="px-2 py-1 text-[10px] font-bold tracking-wide text-black bg-[#faf5cc] border-t border-green-900/10">
                    Account
                  </div>
                  {!isAuthenticated ? (
                    <NavLink to="/login" className="block px-3 py-2 text-xs hover:bg-green-50" role="menuitem" onClick={() => setMenuOpen(false)}>
                      <span className="inline-flex items-center gap-2"><FiLogIn size={14} aria-hidden="true" /> Login</span>
                    </NavLink>
                  ) : (
                    <>
                      <NavLink to="/profile" className="block px-3 py-2 text-xs hover:bg-green-50" role="menuitem" onClick={() => setMenuOpen(false)}>
                        Profile
                      </NavLink>
                      <NavLink to="/dashboard" className="block px-3 py-2 text-xs hover:bg-green-50" role="menuitem" onClick={() => setMenuOpen(false)}>
                        Dashboard
                      </NavLink>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-green-50"
                        role="menuitem"
                        onClick={async () => {
                          setMenuOpen(false);
                          await logout();
                          navigate('/maps');
                        }}
                      >
                        <span className="inline-flex items-center gap-2"><FiLogOut size={14} aria-hidden="true" /> Logout</span>
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs text-red-700 hover:bg-red-50"
                        role="menuitem"
                        onClick={async () => {
                          setMenuOpen(false);
                          await logoutAll();
                          navigate('/maps');
                        }}
                      >
                        Logout all devices
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {isMaps && (
              <>
                <button
                  type="button"
                  onClick={() => dispatchMapEvent('rayat:maps:refresh')}
                  className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded border border-green-900/20 bg-white/70 hover:bg-white text-black text-xs font-semibold"
                  title="Refresh map data"
                >
                  <FiRefreshCw size={14} />
                  Refresh data
                </button>
                <button
                  type="button"
                  onClick={() => dispatchMapEvent('rayat:maps:fit')}
                  className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded border border-green-900/20 bg-white/70 hover:bg-white text-black text-xs font-semibold"
                  title="Fit map to data"
                >
                  <FiMaximize size={14} aria-hidden="true" />
                  Fit all
                </button>
              </>
            )}

            {/* Login / Logout */}
            {!isAuthenticated ? (
              <NavLink
                to="/login"
                className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded border border-green-900/20 bg-white/70 hover:bg-white text-black text-xs font-semibold"
                title="Login"
              >
                <FiLogIn size={14} aria-hidden="true" /> Login
              </NavLink>
            ) : (
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate('/maps');
                }}
                className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded border border-green-900/20 bg-white/70 hover:bg-white text-black text-xs font-semibold"
                title="Logout"
              >
                <FiLogOut size={14} aria-hidden="true" /> Logout{user?.username ? ` (${user.username})` : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
